import { DATIQ_DEFAULT_STRING } from '@dat/datiq-core/models';
import { z } from 'zod';
import { IqCellActionSchema } from '../../datiq-cell-action';
import { DatiqCellChangeSchema } from '../../datiq-cell-change';
import { DatiqCellGraphValueSchema } from '../../datiq-cell-graph-value';
import { DatiqCellHeaderSortSchema } from '../../datiq-cell-header-sort';
import { DatiqCellLabelSchema } from '../../datiq-cell-label';
import { DatiqCellLaneSchema } from '../../datiq-cell-lane';
import { DatiqCellLocationSchema } from '../../datiq-cell-location';
import { IqCellMenuSchema } from '../../datiq-cell-menu';
import { DatiqCellPopupSchema } from '../../datiq-cell-popup';
import { IDatiqCellPopupForm } from '../../datiq-cell-popup-form';
import { DatiqCellTitleSchema } from '../../datiq-cell-title';
import { DatiqCellSelectSchema } from '../../datiq-cell-select';

export const DatiqTableRowCellUnion = z.union([
    DatiqCellChangeSchema,
    DatiqCellGraphValueSchema,
    DatiqCellHeaderSortSchema,
    DatiqCellLabelSchema,
    DatiqCellLaneSchema,
    DatiqCellLocationSchema,
    DatiqCellPopupSchema,
    DatiqCellTitleSchema,
]);

export const DatiqTableRowCellTypesSchema = z.union([
    z.literal('label'),
    z.literal('graph-value'),
    z.literal('lane'),
    z.literal('change'),
    z.literal('popup'),
    z.literal('header-sort'),
    z.literal('title'),
    z.literal('location'),
    z.literal('popup-form'),
]);

export type DatiqTableRowCellTypes = z.infer<
    typeof DatiqTableRowCellTypesSchema
>;
export const DatiqTableRowCellLabelSchema = z.object({
    id: z.string(),
    type: z.literal('label'),
    cell: DatiqCellLabelSchema,
});

export const DatiqTableRowCellGraphValueSchema = z.object({
    id: z.string(),
    type: z.literal('graph-value'),
    cell: DatiqCellGraphValueSchema,
});

export const DatiqTableRowCellLaneSchema = z.object({
    id: z.string(),
    type: z.literal('lane'),
    cell: DatiqCellLaneSchema,
});

export const DatiqTableRowCellChangeSchema = z.object({
    id: z.string(),
    type: z.literal('change'),
    cell: DatiqCellChangeSchema,
});

export const DatiqTableRowCellPopupSchema = z.object({
    id: z.string(),
    type: z.literal('popup'),
    cell: DatiqCellPopupSchema,
});

export const DatiqTableRowCellHeaderSortSchema = z.object({
    id: z.string(),
    type: z.literal('header-sort'),
    cell: DatiqCellHeaderSortSchema,
});

export const DatiqRowTableCellTitleSchema = z.object({
    id: z.string(),
    type: z.literal('title'),
    cell: DatiqCellTitleSchema,
});

export const DatiqRowTableCellLocationSchema = z.object({
    id: z.string(),
    type: z.literal('location'),
    cell: DatiqCellLocationSchema,
});

export const DatiqRowTableCellActionSchema = z.object({
    id: z.string(),
    type: z.literal('action'),
    cell: IqCellActionSchema
});

export const DatiqRowTableCellMenuSchema = z.object({
    id: z.string(),
    type: z.literal('menu'),
    cell: IqCellMenuSchema
})

export const DatiqRowTableCellSelectSchema = z.object({
    id: z.string(),
    type: z.literal('select'),
    cell: DatiqCellSelectSchema
});

export const DatiqTableRowCellSchema = z.discriminatedUnion('type', [
    DatiqTableRowCellChangeSchema,
    DatiqTableRowCellGraphValueSchema,
    DatiqTableRowCellHeaderSortSchema,
    DatiqTableRowCellLabelSchema,
    DatiqTableRowCellLaneSchema,
    DatiqRowTableCellLocationSchema,
    DatiqTableRowCellPopupSchema,
    DatiqRowTableCellTitleSchema,
    DatiqRowTableCellActionSchema,
    DatiqRowTableCellMenuSchema,
    DatiqRowTableCellSelectSchema
]);

const PopUpFormKeySchema = z.literal('popup-form');

export type PopUpFormKey = z.infer<typeof PopUpFormKeySchema>;

export type IDatiqTableRowCell =
    | z.infer<typeof DatiqTableRowCellSchema>
    | {
          id: string;
          type: PopUpFormKey;
          cell: IDatiqCellPopupForm;
      };
export type DatiqTableRowCell =
    | z.infer<typeof DatiqTableRowCellSchema>
    | {
          id: string;
          type: PopUpFormKey;
          cell: IDatiqCellPopupForm;
      };
export const DatiqTableRowColumnsSchema = z.record(
    z.string(),
    DatiqTableRowCellSchema,
);
export const DatiqTableRowSchema = z.object({
    colOrder: z.array(z.unknown()),
    columns: DatiqTableRowColumnsSchema,
    data: z.unknown(),
    id: z.string(),
    index: z.number(),
    staticColumns: z.number().optional(),
    selectedHeader: z.boolean().optional(),
    isContribution: z.boolean().optional(),
});

export interface IDatiqTableRowColumns
    extends z.infer<typeof DatiqTableRowColumnsSchema> {}

export interface IDatiqTableRow extends z.infer<typeof DatiqTableRowSchema> {}

export type IDatiqCellLaneDatiqTableRowCell =
    | z.infer<typeof DatiqTableRowCellSchema>
    | IDatiqCellPopupForm;

// export interface IDatiqTableRow {
//     colOrder: unknown[];
//     columns: Record<string, >;
//     data: unknown;
//     id: string;
//     index: number;
//     staticColumns?: number;
// }

export const DatiqRowCellInteraction = z.object({
    id: z.string(),
    data: z.unknown(),
    row: DatiqTableRowSchema,
    cell: DatiqTableRowCellSchema,
});

export interface IDatiqRowCellInteraction
    extends z.infer<typeof DatiqRowCellInteraction> {}

export const emptyDatiqTableRow: IDatiqTableRow = {
    colOrder: [],
    columns: {} as IDatiqTableRowColumns,
    data: {},
    id: DATIQ_DEFAULT_STRING,
    index: 0,
    selectedHeader: false,
};
